import { LABEL_FLOOR_PX, PT_PER_CSS_PX } from './exportLabelTypes'
import { READ_SCALE_BY_SHEET, READ_SCALE_RUNGS } from './exportReadScale'
import {
  ExportPrintRow,
  ExportPrintTextKind,
  ExportScaleBinding,
  ExportSymbolKind,
  ExportTextKind,
  IExportKindValues,
  IExportPrintedLength,
  IExportTextKindSize,
  printAutoPx,
} from './exportTextScale'
import {
  CSS_PX_PER_INCH,
  ExportPaperPreset,
  METRIC_PAPER_PRESETS,
  MM_PER_INCH,
} from './mapExportPaper'

// Adjust by type: a pin or icon size, or a text kind.
export type ExportAdjustRow =
  | { type: 'symbol'; kind: ExportSymbolKind }
  | { type: 'text'; kind: ExportTextKind }

// What each row sizes is drawn, so the row is listed.
export interface IExportAdjustShown {
  workflowPins: boolean
  workflowLabels: boolean
  locationMarkers: boolean
  locationLabels: boolean
  workflowMarkupLabels: boolean
  // Markups shown and some text markup drawn.
  markupTexts: boolean
  arePageElementsShown: boolean
}

interface IExportAdjustEntry {
  row: ExportAdjustRow
  // Key and numbers has none: it stays listed, so flipping Label key below it moves nothing.
  shownWith: keyof IExportAdjustShown | null
}

// Workflows, Locations, Markups, then the key and the band.
const ADJUST_GROUPS: IExportAdjustEntry[][] = [
  [
    { row: { type: 'symbol', kind: 'workflowPin' }, shownWith: 'workflowPins' },
    { row: { type: 'text', kind: 'workflowPin' }, shownWith: 'workflowLabels' },
  ],
  [
    { row: { type: 'symbol', kind: 'location' }, shownWith: 'locationMarkers' },
    { row: { type: 'text', kind: 'location' }, shownWith: 'locationLabels' },
  ],
  [
    {
      row: { type: 'text', kind: 'markup' },
      shownWith: 'workflowMarkupLabels',
    },
    { row: { type: 'text', kind: 'markupText' }, shownWith: 'markupTexts' },
  ],
  [
    { row: { type: 'text', kind: 'key' }, shownWith: null },
    { row: { type: 'text', kind: 'band' }, shownWith: 'arePageElementsShown' },
  ],
]

// What only a page with Workflows draws: Workflow pins and names, Markups.
const WORKFLOW_SHOWN_WITH: ReadonlySet<keyof IExportAdjustShown> = new Set([
  'workflowPins',
  'workflowLabels',
  'workflowMarkupLabels',
  'markupTexts',
])

// Every row the page can draw, in sight or not; a page without Workflows (Map Setup) has none of
// theirs.
export const drawableAdjustRows = (hasWorkflows: boolean): ExportAdjustRow[] =>
  ADJUST_GROUPS.flat()
    .filter(
      ({ shownWith }) => hasWorkflows || !WORKFLOW_SHOWN_WITH.has(shownWith),
    )
    .map(({ row }) => row)

// The rows in sight, by group; a group with none is left out, so it takes no gap.
export const shownAdjustGroups = (
  shown: IExportAdjustShown,
): ExportAdjustRow[][] =>
  ADJUST_GROUPS.map(group =>
    group
      .filter(({ shownWith }) => !shownWith || shown[shownWith])
      .map(({ row }) => row),
  ).filter(group => group.length > 0)

// A row that holds a percent: every row on Screen, and Markup text, which follows the map's zoom.
export const isPercentRow = (row: ExportAdjustRow, isPrint: boolean): boolean =>
  !isPrint || (row.type === 'text' && row.kind === 'markupText')

const TEXT_STOPS_PT = [
  6.5, 7, 8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 28, 32, 36, 42, 48, 54,
  60, 66, 72, 84, 96, 108,
]
// The reach before sizes could be set: half to twice a row's size at every distance.
const REACH = { min: 0.5, max: 2 }

const range = (from: number, to: number): number[] =>
  Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i)

// A Print row's stops, in sheet px, over the sizes it reaches on this sheet: text in common point
// sizes, a pin or icon in whole millimetres on metric paper, else twentieths of an inch.
export function printStopsPx(
  row: ExportPrintRow,
  paper: ExportPaperPreset,
): number[] {
  const maxPx = REACH.max * printAutoPx(row, READ_SCALE_BY_SHEET[paper].maximum)
  if (row.type === 'text') {
    return TEXT_STOPS_PT.map(pt => pt / PT_PER_CSS_PX).filter(
      px => px >= LABEL_FLOOR_PX - 1e-6 && px <= maxPx + 1e-6,
    )
  }
  const minPx = REACH.min * printAutoPx(row, READ_SCALE_RUNGS[0])
  const pxPerUnit = METRIC_PAPER_PRESETS.includes(paper)
    ? CSS_PX_PER_INCH / MM_PER_INCH
    : CSS_PX_PER_INCH / 20
  return range(Math.ceil(minPx / pxPerUnit), Math.floor(maxPx / pxPerUnit)).map(
    units => units * pxPerUnit,
  )
}

// The stop a size sits on, or the nearest.
export const nearestStop = (stopsPx: number[], px: number): number =>
  stopsPx.reduce(
    (best, stop, index) =>
      Math.abs(stop - px) < Math.abs(stopsPx[best] - px) ? index : best,
    0,
  )

export interface IExportAdjustReadout {
  value: number
  unit: 'percent' | 'pt' | IExportPrintedLength['unit']
  // Text rows only, and only for a kind the export prints.
  binding: ExportScaleBinding
  // A Print row that follows the distance, no size set.
  isAuto: boolean
}

// Print reads a text row in pt and a pin or icon in its printed length, and whether it follows the
// distance; a percent row reads its percent. A floor or a fit holds text on Screen too, so a held
// row is marked on both.
export function resolveAdjustReadout(
  row: ExportAdjustRow,
  readings: {
    isPrint: boolean
    values: IExportKindValues
    textSizes: Record<ExportTextKind, IExportTextKindSize>
    symbolSizes: Record<ExportSymbolKind, IExportPrintedLength>
    printedKinds: ExportTextKind[]
  },
): IExportAdjustReadout {
  const { isPrint, values } = readings
  if (row.type === 'symbol') {
    return isPrint
      ? {
          ...readings.symbolSizes[row.kind],
          binding: null,
          isAuto: values.symbolPx[row.kind] === undefined,
        }
      : {
          value: values.symbolPercent[row.kind],
          unit: 'percent',
          binding: null,
          isAuto: false,
        }
  }
  const size = readings.textSizes[row.kind]
  const binding = readings.printedKinds.includes(row.kind) ? size.binding : null
  if (isPercentRow(row, isPrint)) {
    return {
      value: values.textPercent[row.kind],
      unit: 'percent',
      binding,
      isAuto: false,
    }
  }
  return {
    value: size.points,
    unit: 'pt',
    binding,
    isAuto: values.textPx[row.kind as ExportPrintTextKind] === undefined,
  }
}
